import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StormDateDropDown from '../StormDateDropDown';
import { useMapContext } from '../Map/MapContext';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createDevelopmentPlan, deleteDevelopmentPlan } from '../../graphql/mutations';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const DevelopmentPlanSelector = () => {
    const {
        userData,
        devPlanID,
        setDevPlanID,
        setUserData
    } = useMapContext();

    const [isNewPlanModalOpen, setIsNewPlanModalOpen] = useState(false);
    const [newPlanName, setNewPlanName] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [nameError, setNameError] = useState('');

    const handleChange = async (event) => {
        const selectedValue = event.target.value;
        if (selectedValue === 'create_new') {
            setIsNewPlanModalOpen(true);
        } else {
            setDevPlanID(selectedValue);
        }
    };

    const handleDeleteClick = (event, plan) => {
        event.stopPropagation();
        setPlanToDelete(plan);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!planToDelete) return;

        try {
            await client.graphql({
                query: deleteDevelopmentPlan,
                variables: { input: { id: planToDelete.id } }
            });

            // HERE IS WHERE WE WANT TO DELETE THE INP FILE GLWA_{STORM}_{USERID}_{DEVPLANID}_{VERSION}.inp
            // https://2471d2z813.execute-api.us-east-2.amazonaws.com//delete-inp/{file_name}

            // Update local state
            setUserData(prevData => ({
                ...prevData,
                developmentPlans: {
                    ...prevData.developmentPlans,
                    items: prevData.developmentPlans.items.filter(plan => plan.id !== planToDelete.id)
                }
            }));

            if (devPlanID === planToDelete.id) {
                setDevPlanID(10);
            }
        } catch (error) {
            console.error('Error deleting development plan:', error);
        }

        setIsDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const handleDeleteCancel = () => {
        setIsDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const handleCancelNewPlan = () => {
        setIsNewPlanModalOpen(false);
        setNewPlanName('');
        setNameError('');
    };

    const handleNewPlanNameChange = (e) => {
        const value = e.target.value;
        setNewPlanName(value);
        
        if (value.trim() === '') {
            setNameError('');
        } else if (userData?.developmentPlans?.items.some(plan => 
            plan.name.toLowerCase() === value.trim().toLowerCase()
        )) {
            setNameError('A plan with this name already exists.');
        } else {
            setNameError('');
        }
    };

    const handleCreateNewPlan = async () => {
        if (!userData || !userData.id || !newPlanName.trim()) {
            console.error('Cannot create development plan: User ID is missing or plan name is empty');
            return;
        }
        try {

            // Check if the plan name already exists
            const existingPlanNames = userData.developmentPlans.items.map(plan => plan.name.toLowerCase());
            if (existingPlanNames.includes(newPlanName.trim().toLowerCase())) {
                // setErrorMessage('You have already created a development plan with this name.');
                // console.error('A development plan with this name already exists');
                // You might want to show an error message to the user here
                return;
            }

            const newPlan = {
                id: uuidv4(),
                name: newPlanName.trim(),
                version: 1,
                userID: userData.id
            };
            const result = await client.graphql({
                query: createDevelopmentPlan,
                variables: { input: newPlan }
            });
            console.log('Created new development plan:', result.data.createDevelopmentPlan);
            
            const createdPlan = result.data.createDevelopmentPlan;
            // Update local state
            setUserData(prevData => ({
                ...prevData,
                developmentPlans: {
                    ...prevData.developmentPlans,
                    items: [...prevData.developmentPlans.items, createdPlan]
                }
            }));
            setDevPlanID(createdPlan.id);
            console.log("The devPlanID is now: ", devPlanID);
            setIsNewPlanModalOpen(false);
            setNewPlanName('');
        } catch (error) {
            console.error('Error creating development plan:', error);
            // setErrorMessage('An error occurred while creating the development plan');
        }
    };

    const renderValue = (selected) => {
        const devPlanID = userData?.developmentPlans?.items.find(plan => plan.id === selected);
        return devPlanID ? devPlanID.name : '';
    };

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 60,
                zIndex: 10,
                borderRadius: '5px',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
                <InputLabel id="development-plan-select-label">Development Plan</InputLabel>
                <Select
                    key={devPlanID}
                    labelId="development-plan-select-label"
                    id="development-plan-select"
                    value={devPlanID}
                    onChange={handleChange}
                    renderValue={renderValue}
                    label="Development Plan"
                >
                    {userData && userData.developmentPlans && userData.developmentPlans.items.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                {plan.name}
                                {devPlanID === plan.id && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => handleDeleteClick(event, plan)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </div>
                        </MenuItem>
                    ))}
                    <MenuItem value="create_new" sx={{ color: 'primary.main' }}>
                        <AddIcon sx={{ mr: 1 }} />
                        Create New Plan
                    </MenuItem>
                </Select>
            </FormControl>
            <StormDateDropDown />

            <Dialog open={isNewPlanModalOpen} onClose={() => setIsNewPlanModalOpen(false)}>
                <DialogTitle>Create New Development Plan</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Plan Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={newPlanName}
                        onChange={handleNewPlanNameChange}
                        error={!!nameError}
                        helperText={nameError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelNewPlan}>Cancel</Button>
                    <Button 
                        onClick={handleCreateNewPlan}
                        disabled={!!nameError || !newPlanName.trim()}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Development Plan?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the plan "{planToDelete?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default DevelopmentPlanSelector;